import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "../i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1558694440-03ade9215d7b?q=80&w=2400&auto=format&fit=crop";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero({ scrollTo, onDonate }) {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0">
        <img
          src={HERO_IMG}
          alt="Baobab trees at sunset in Madagascar"
          className="h-[115%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#04132e]/60" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#04132e] to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-5 pb-16 pt-40 md:px-8"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="mb-6 text-[12px] font-semibold uppercase tracking-[0.35em] text-primary"
          data-testid="hero-kicker"
        >
          {t.hero.kicker}
        </motion.p>

        <h1 className="font-serif text-5xl leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-8xl">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                data-testid={`hero-title-line-${i}`}
                className={`block ${i === t.hero.lines.length - 1 ? "italic text-primary" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25 + i * 0.16, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-white/75 md:text-lg"
          data-testid="hero-subtitle"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-donate-button"
            onClick={onDonate}
            className="rounded-full bg-primary px-9 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-white shadow-xl shadow-primary/30 transition-all duration-300 hover:-translate-y-1 hover:bg-[#e0562a]"
          >
            {t.hero.ctaPrimary}
          </button>
          <button
            data-testid="hero-about-button"
            onClick={() => scrollTo("chi-siamo")}
            className="rounded-full border border-white/25 px-9 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:border-white/60 hover:bg-white/5"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.25 }}
          className="mt-16 flex flex-wrap gap-x-14 gap-y-6 border-t border-white/10 pt-8"
          data-testid="hero-stats"
        >
          {t.hero.stats.map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <div className="font-serif text-4xl text-white md:text-5xl">{s.value}</div>
              <div className="mt-1 text-[12px] font-medium uppercase tracking-[0.2em] text-white/50">
                {s.label}
              </div>
            </div>
          ))}
          <div className="ml-auto hidden items-end md:flex">
            <button
              data-testid="hero-scroll-indicator"
              onClick={() => scrollTo("chi-siamo")}
              className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.25em] text-white/50 transition-colors hover:text-white"
            >
              {t.hero.scroll}
              <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
                <ArrowDown size={14} />
              </motion.span>
            </button>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
